/**
 * `ActivityTimeline` — an ordered sequence of things that happened, and
 * `Stepper` — an ordered sequence of things that must happen.
 *
 * Both carry a status per item and both refuse to let `skipped` look like
 * `pass`. That is a standing rule in this codebase rather than a style choice:
 * a preflight gate that could not be evaluated, a validator family that did not
 * run, a DNS job parked in `blocked` — each is a check whose answer is unknown,
 * and rendering unknown as success is how an operator ships a broken fleet with
 * a green screen in front of them.
 */

import type { ReactNode } from 'react';

import type { Tone } from '../Badge';

export type TimelineStatus = 'ok' | 'failed' | 'skipped' | 'pending' | 'active' | 'info';

const STATUS_TONE: Record<TimelineStatus, Tone> = {
  ok: 'good',
  failed: 'critical',
  skipped: 'warning',
  pending: 'neutral',
  active: 'info',
  info: 'neutral',
};

/** Distinct silhouettes, as with badges — never colour alone. */
function Mark({ status }: { status: TimelineStatus }) {
  const tone = STATUS_TONE[status];
  return (
    <span className={`timeline__mark timeline__mark--${tone}`} aria-hidden="true">
      <svg viewBox="0 0 12 12" width="12" height="12" focusable="false">
        {status === 'ok' ? (
          <path d="M2.5 6.2 5 8.7l4.6-5" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
        ) : status === 'failed' ? (
          <path d="M3.2 3.2 8.8 8.8M8.8 3.2 3.2 8.8" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
        ) : status === 'skipped' ? (
          <path d="M3 6h6" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
        ) : status === 'active' ? (
          <circle cx="6" cy="6" r="3.2" fill="currentColor" />
        ) : (
          <circle cx="6" cy="6" r="3" fill="none" stroke="currentColor" strokeWidth="1.6" />
        )}
      </svg>
    </span>
  );
}

export interface TimelineItem {
  id: string;
  status: TimelineStatus;
  title: ReactNode;
  detail?: ReactNode;
  /** Pre-formatted; callers use `format.ts` so nulls stay em-dashes. */
  time?: ReactNode;
  children?: ReactNode;
}

export function ActivityTimeline({
  items,
  label,
}: {
  items: readonly TimelineItem[];
  label: string;
}) {
  return (
    <ol className="timeline" aria-label={label}>
      {items.map((item) => (
        <li key={item.id} className="timeline__item">
          <Mark status={item.status} />
          <div className="timeline__body">
            <div className="timeline__head">
              <span className="timeline__title">{item.title}</span>
              {item.time ? <span className="timeline__time">{item.time}</span> : null}
            </div>
            {item.detail ? <p className="timeline__detail">{item.detail}</p> : null}
            {item.children}
          </div>
        </li>
      ))}
    </ol>
  );
}

export interface Step {
  id: string;
  label: ReactNode;
  status: TimelineStatus;
  detail?: ReactNode;
}

/**
 * The horizontal progress rail for a multi-step workflow.
 *
 * `aria-current="step"` marks the active one; each step's status is also given
 * as text in a visually-hidden span, so the rail is not a row of coloured dots
 * to a screen reader.
 */
export function Stepper({
  steps,
  label,
}: {
  steps: readonly Step[];
  label: string;
}) {
  return (
    <ol className="stepper" aria-label={label}>
      {steps.map((step, index) => (
        <li
          key={step.id}
          className={`stepper__step stepper__step--${STATUS_TONE[step.status]}`}
          aria-current={step.status === 'active' ? 'step' : undefined}
        >
          <span className="stepper__index">
            {step.status === 'ok' ? (
              <svg viewBox="0 0 12 12" width="12" height="12" aria-hidden="true" focusable="false">
                <path d="M2.5 6.2 5 8.7l4.6-5" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            ) : (
              index + 1
            )}
          </span>
          <span className="stepper__text">
            <span className="stepper__label">{step.label}</span>
            <span className="visually-hidden">{` — ${step.status}`}</span>
            {step.detail ? <span className="stepper__detail">{step.detail}</span> : null}
          </span>
        </li>
      ))}
    </ol>
  );
}
